import { FC } from 'react';
import styled from '@emotion/styled';
import { Button } from 'components/core/button';
import { ExternalLink } from 'components/core/link/Link';
import { H3, Text } from 'components/core/text/Text';
import { spacing } from 'components/core/theme';
import { Modal, ModalBody } from 'components/modal/Modal';
import { BendHelpLine } from 'helpers/config/constants';
import FailGus from 'public/assets/img/buddies/fail-headscratch-circle.gif';

import { EligibilityErrorType } from './../eligibility/EligibilityError';

const PhoneWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: ${spacing.xs};
`;

interface Props {
  setEligibilityError(error: EligibilityErrorType | undefined): void;
  showHelpPage(allowBack: boolean): void;
  doNotCheckEligibility(): void;
}

export const EligibilityCheckNotWorkingModal: FC<Props> = ({
  setEligibilityError,
  showHelpPage,
  doNotCheckEligibility,
}) => {
  return (
    <Modal noSneezeColor onClose={() => setEligibilityError(undefined)}>
      <ModalBody className="max-w-[458px]">
        <img src={FailGus} className="pb-6 h-[150px]" />
        <H3>Hmm, this isn&apos;t working.</H3>
        <Text className="pb-8 text-center" block variant="text2">
          Let&apos;s try something different.
        </Text>
        <div className="flex gap-4 justify-center w-full flex-col">
          <Button onClick={() => showHelpPage(true)}>
            Help me with my insurance
          </Button>
          <Button variant="secondary" onClick={doNotCheckEligibility}>
            Continue without insurance
          </Button>
        </div>
      </ModalBody>
      <PhoneWrapper>
        <Text>Think something&apos;s wrong?&nbsp;</Text>
        <Text bold>
          Call us at&nbsp;
          <ExternalLink bold href={`tel:${BendHelpLine.rawNum}`}>
            {BendHelpLine.formattedNum}
          </ExternalLink>
        </Text>
      </PhoneWrapper>
    </Modal>
  );
};
export const EligibilityCheckFailedModal: FC<Props> = ({
  setEligibilityError,
  showHelpPage,
  doNotCheckEligibility,
}) => {
  return (
    <Modal noSneezeColor onClose={() => setEligibilityError(undefined)}>
      <ModalBody className="max-w-[458px]">
        <img src={FailGus} className="pb-6 h-[150px]" />
        <H3>Oh no!</H3>
        <Text className="pb-8 text-center" block variant="text2">
          Looks like your eligibility check failed.
        </Text>
        <Text block variant="text4" className="pb-4">
          Here are some things to double check:
        </Text>
        <ul className="pl-4 pb-8 list-disc">
          <li>
            <Text variant="text4">
              Did you enter the correct{' '}
              <Text variant="text4" bold>
                birthdate?
              </Text>
            </Text>
          </li>
          <li>
            <Text variant="text4">
              Did you enter the correct{' '}
              <Text variant="text4" bold>
                group ID?
              </Text>
            </Text>
          </li>
          <li>
            <Text variant="text4">
              Did you enter the correct{' '}
              <Text variant="text4" bold>
                payer ID?
              </Text>
            </Text>
          </li>
        </ul>
        <div className="flex gap-4 justify-center w-full flex-col">
          <Button onClick={() => setEligibilityError(undefined)}>
            Edit information and try again
          </Button>
          <Button variant="secondary" onClick={() => showHelpPage(true)}>
            Help me with my insurance
          </Button>
          <Button variant="secondary" onClick={doNotCheckEligibility}>
            Continue without insurance
          </Button>
        </div>
      </ModalBody>
      <PhoneWrapper>
        <Text>Think something&apos;s wrong?&nbsp;</Text>
        <Text bold>
          Call us at&nbsp;
          <ExternalLink bold href={`tel:${BendHelpLine.rawNum}`}>
            {BendHelpLine.formattedNum}
          </ExternalLink>
        </Text>
      </PhoneWrapper>
    </Modal>
  );
};
